import type { Language } from '../contexts/LanguageContext';

const translations = {
  zh: {
    // Navbar
    nav: {
      brand: '大模型PLUS',
      langToggle: 'EN',
    },

    // Hero
    hero: {
      teamName: 'ControlFoley',
      tagline: '致力于音频算法研究，更强模型，更优效果',
      features: [
        '音效生成：提供视频配音、文本生成音频、文本 / 音频控制视频配音等音视频同步生成能力',
        // '音频编辑：支持基于文本与视频的音频删除、提取、替换、补充等编辑操作',
      ],
      cta: '立即体验',
    },

    // ProductCapabilities
    product: {
      sectionTitle: '多元音频创作能力',
      tabs: {
        gen: { label: '音效生成', title: '音效生成能力', features: [
          '视频内容自适应配音，自动生成匹配音效',
          '多模态驱动，支持参考音频 + 文本指令实现风格迁移',
          '文本生成高保真音频，还原真实物理环境声音',
        ]},
        edit: { label: '音效编辑', title: '智能音效编辑', features: [
          '音效删除，精准删除视频中对象声音',
          '音效提取，智能提取视频中对象声音',
          '音效替换，无痕替换对象声音',
          '音效增加，补充或增加目标音频',
        ]},
      },
      learnMore: '能力介绍',
      tryNow: '立即体验',
    },

    // Common
    common: {
      backHome: '返回主页',
      coreCapability: '核心能力',
      tryNow: '立即体验',
      learnMore: '了解更多',
    },

    // SoundGenerationIntro
    genIntro: {
      badge: '核心能力',
      title: '音效生成能力',
      titleEn: 'Audio Generation',
      desc: '基于自研大规模音频生成模型，理解物理世界的声音规律，实现从文本、视频到高保真音效的跨模态生成，为内容创作注入灵魂。',
      tag1: '文生音效',
      tag2: '视频生音效',
      tag3: '文本控制视频生音效',
      tag4: '音频控制视频生音效',
      techTitle: '技术特性',
      feature1Title: '多模态对齐',
      feature1Desc: '深度融合视觉与听觉特征，能够精确识别视频中的动作、材质与环境，生成时间轴精准对齐的音效。',
      feature2Title: '高保真音质',
      feature2Desc: '支持 48kHz 采样率音频生成，频响范围覆盖人耳听觉全频段，还原真实物理世界的细腻听感。',
      feature3Title: '可控生成',
      feature3Desc: '支持通过文本提示词（Prompt）精确控制生成音效的风格、情绪与具体的声学事件细节。',
      listenTitle: '聆听下方逼真的文生音效',
      categories: {
        natural: '自然音效',
        instrument: '乐器音效',
        animal: '动物音效',
        human: '人类音效',
        other: '其他音效',
      },
      sounds: {
        stream: '小河流水', raindrop: '雨滴声', wave: '海浪声', insect: '虫鸣',
        campfire: '篝火', thunder: '雷声', guitar: '吉他', drum: '架子鼓',
        piano: '钢琴', sheep: '羊叫', dog: '狗叫', cat: '猫叫',
        footsteps: '脚步声', typing: '打字声', snore: '打呼噜',
        motorcycle: '摩托车引擎', alarm: '闹钟声',
      },
      carousels: { film: '影视创作', ecommerce: '电商营销', creative: '创意玩法' },
    },

    // SoundEditingIntro
    editIntro: {
      badge: '核心能力',
      title: '智能音效编辑',
      titleEn: 'Intelligent Sound Editing',
      desc: '基于多模态大模型，实现对视频中音频内容的精准理解与控制，支持多维度音效编辑操作，让音频创作更自由。',
      tag1: '音效删除',
      tag2: '音效替换',
      tag3: '音效添加',
      tag4: '交互式生成',
      promptLabel: '提示词',
      inputLabel: '输入',
      outputLabel: '输出',
      techTitle: '技术特性',
    },

    // AIExperiencePage
    experience: {
      tabs: {
        gen: { name: '音效生成', sub: '音效所至，场景自成' },
        edit: { name: '音频编辑', sub: '智能创作，音频修饰' },
      },
      backHome: '返回主页',
      editTitle: '智能音频编辑，为创作而生',
      genTitle: '立即体验，让创意发声',
      genModes: {
        text: '文生音效',
        video: '视频生音效',
        ref: '参考音频生音效',
      },
      config: {
        modelLabel: '模型：',
        modelSelect: '模型选择',
        durationLabel: '时长：',
        durationGen: '生成时长',
        bySeconds: '按秒数',
        smartDuration: '智能时长',
        smartShort: '智能',
        sec: '秒',
        countLabel: '数量：',
        countSelect: '选择生成数量',
        item: '条',
        editOptions: '编辑选项',
        uploadVideoClick: '点击上传视频',
        uploadRefAudioClick: '点击上传参考音频',
      },
      tryDemos: {
        textTitle: 'Text-to-Audio 演示',
        videoTitle: 'Video-to-Audio 演示',
        textCtrlTitle: 'Text-Controlled Video-to-Audio 演示',
        refTitle: 'Audio-Controlled Video-to-Audio 演示',
        promptLabel: '提示词：'
      },
      status: {
        submitting: '提交任务中',
        queued: '排队中',
        processing: '正在生成',
        waiting: '等待结果',
        failed: '生成失败',
        done: '生成完成',
      },
      runtime: {
        creating: '创作中',
      },
    },
  },

  en: {
    // Navbar
    nav: {
      brand: '大模型PLUS',
      langToggle: '中',
    },

    // Hero
    hero: {
      teamName: 'ControlFoley',
      tagline: 'Dedicated to audio algorithm research, stronger models, better results',
      features: [
        'Audio Generation: Video dubbing, text-to-audio, and text/audio-controlled video dubbing with AV sync',
        // 'Audio Editing: Text and video based audio deletion, extraction, replacement, and supplementation',
      ],
      cta: 'Try Now',
    },

    // ProductCapabilities
    product: {
      sectionTitle: 'Diverse Audio Creation Capabilities',
      tabs: {
        gen: { label: 'Audio Generation', title: 'Audio Generation', features: [
          'Video-adaptive dubbing, automatically generates matching sound effects',
          'Multi-modal driven, supports reference audio + text instructions for style transfer',
          'Text-to-high-fidelity audio, restoring real-world physical sounds',
        ]},
        edit: { label: 'Audio Editing', title: 'Intelligent Audio Editing', features: [
          'Sound deletion, precisely remove target sounds from video',
          'Sound extraction, intelligently extract target sounds from video',
          'Sound replacement, seamlessly replace target sounds',
          'Sound addition, supplement or add target audio',
        ]},
      },
      learnMore: 'Learn More',
      tryNow: 'Try Now',
    },

    // Common
    common: {
      backHome: 'Back to Home',
      coreCapability: 'Core Capability',
      tryNow: 'Try Now',
      learnMore: 'Learn More',
    },

    // SoundGenerationIntro
    genIntro: {
      badge: 'Core Capability',
      title: 'Audio Generation',
      titleEn: 'Audio Generation',
      desc: 'Based on our proprietary large-scale audio generation model, understanding the acoustic laws of the physical world, enabling cross-modal generation from text and video to high-fidelity sound effects.',
      tag1: 'Text-to-Audio',
      tag2: 'Video-to-Audio',
      tag3: 'Text-Controlled Video-to-Audio',
      tag4: 'Audio-Controlled Video-to-Audio',
      techTitle: 'Technical Features',
      feature1Title: 'Multimodal Alignment',
      feature1Desc: 'Deeply fuses visual and auditory features, accurately identifying actions, materials, and environments in video to generate time-aligned sound effects.',
      feature2Title: 'High-Fidelity Audio',
      feature2Desc: 'Supports 48kHz sample rate audio generation, covering the full human hearing frequency range, restoring the nuanced sensation of the real world.',
      feature3Title: 'Controllable Generation',
      feature3Desc: 'Supports precise control of generated sound style, emotion, and specific acoustic event details through text prompts.',
      listenTitle: 'Listen to the Realistic Text-to-Sound Effects Below',
      categories: {
        natural: 'Nature',
        instrument: 'Instruments',
        animal: 'Animals',
        human: 'Human',
        other: 'Other',
      },
      sounds: {
        stream: 'Stream', raindrop: 'Raindrops', wave: 'Ocean Waves', insect: 'Insects',
        campfire: 'Campfire', thunder: 'Thunder', guitar: 'Guitar', drum: 'Drum Kit',
        piano: 'Piano', sheep: 'Sheep', dog: 'Dog', cat: 'Cat',
        footsteps: 'Footsteps', typing: 'Typing', snore: 'Snoring',
        motorcycle: 'Motorcycle Engine', alarm: 'Alarm Clock',
      },
      carousels: { film: 'Film & TV', ecommerce: 'E-commerce', creative: 'Creative' },
    },

    // SoundEditingIntro
    editIntro: {
      badge: 'Core Capability',
      title: 'Intelligent Sound Editing',
      titleEn: 'Intelligent Sound Editing',
      desc: 'Based on multimodal large models, achieving precise understanding and control of audio content in video, supporting multi-dimensional audio editing operations.',
      tag1: 'Sound Deletion',
      tag2: 'Sound Replacement',
      tag3: 'Sound Addition',
      tag4: 'Interactive Generation',
      promptLabel: 'Prompt',
      inputLabel: 'Input',
      outputLabel: 'Output',
      techTitle: 'Technical Features',
    },

    // AIExperiencePage
    experience: {
      tabs: {
        gen: { name: 'Audio Generation', sub: 'Where audio meets scene' },
        edit: { name: 'Audio Editing', sub: 'Smart creation, audio refinement' },
      },
      backHome: 'Back to Home',
      editTitle: 'Intelligent Audio Editing, Built for Creativity',
      genTitle: 'Try It Now, Let Creativity Speak',
      genModes: {
        text: 'Text-to-Sound',
        video: 'Video-to-Sound',
        ref: 'Reference Audio-to-Sound',
      },
      config: {
        modelLabel: 'Model:',
        modelSelect: 'Model',
        durationLabel: 'Duration:',
        durationGen: 'Duration',
        bySeconds: 'By Seconds',
        smartDuration: 'Smart',
        smartShort: 'Smart',
        sec: 's',
        countLabel: 'Count:',
        countSelect: 'Count',
        item: '',
        editOptions: 'Edit Options',
        uploadVideoClick: 'Click to upload video',
        uploadRefAudioClick: 'Click to upload reference audio',
      },
      tryDemos: {
        textTitle: 'Text-to-Audio demos',
        videoTitle: 'Video-to-Audio demos',
        textCtrlTitle: 'Text-Controlled Video-to-Audio demos',
        refTitle: 'Audio-Controlled Video-to-Audio demos',
        promptLabel: 'Prompt:'
      },
      status: {
        submitting: 'Submitting task',
        queued: 'In queue',
        processing: 'Generating',
        waiting: 'Waiting for result',
        failed: 'Failed',
        done: 'Completed',
      },
      runtime: {
        creating: 'Creating',
      },
    },
  },
} as const;

export type Translations = typeof translations['zh'];
export type TranslationKey = keyof Translations;

export function getT(lang: Language) {
  return translations[lang];
}

export default translations;
